// validationSchemas.js - Validation schemas for all report types
import * as Yup from 'yup';

const ONE_YEAR_AGO = new Date();
ONE_YEAR_AGO.setFullYear(ONE_YEAR_AGO.getFullYear() - 1);

// Comprehensive blocklist of free and disposable temp email providers
const blockedDomains = [
  // Major Free Email Providers
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com', 'live.com', 'msn.com', 'ymail.com', 'rocketmail.com', 'mail.com', 'zoho.com', 'proton.me', 'protonmail.com', 'yandex.com', 'gmx.com',
  // Popular Disposable / Temp Email Providers
  'tempmail.com', '10minutemail.com', 'guerrillamail.com', 'mailinator.com', 'yopmail.com', 'trashmail.com', 'temp-mail.org', 'throwawaymail.com', 'getnada.com', 'dispostable.com', 'maildrop.cc', 'sharklasers.com', 'anonbox.net', 'minuteinbox.com', 'tempail.com', 'fakemail.net', 'tempmail.net', 'mohakt.com', 'tmpmail.org'
];

export const organizationValidationSchema = Yup.object().shape({
  // Optional fields
  full_name: Yup.string()
    .nullable()
    .min(2, "Name too short")
    .max(50, "Name too long"),
  phone_number: Yup.string()
    .nullable()
    .test('phone-format', 'Phone number must be 10 digits', (value) => {
      if (!value || value.trim() === '') return true;
      return /^[0-9]{10}$/.test(value);
    }),
  address: Yup.string().nullable(),
  job_title: Yup.string(),
  incident_description: Yup.string()
    .min(50, "Please provide more details (minimum 50 characters)"),
  incident_files: Yup.mixed(),

  email: Yup.string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .email("Invalid email")
    .nullable()
    .test("is-company-email", "Please use a company email address", (value) => {
      if (!value) return true;
      const domain = value.split('@')[1]?.toLowerCase();
      return !blockedDomains.includes(domain);
    }),
  organization_name: Yup.string().required("Organization name is required"),
  organization_type: Yup.string().required("Organization type is required"),
  attack_type: Yup.string().required("Attack type is required"),
  system_functions: Yup.array()
    .min(1, "Please select at least one system function")
    .required("This field is required"),
  impact: Yup.string().required("Impact level is required"),
  time: Yup.date()
    .required('Time is required')
    .max(new Date(), 'Time cannot be in the future')
    .min(ONE_YEAR_AGO, 'Time cannot be more than 1 year ago'),
  affected_systems: Yup.number().required("Number of affected systems is required").min(1, "At least one system must be affected"),
  recaptcha: Yup.string().required("Please complete the reCAPTCHA verification"),
});

export const personalValidationSchema = Yup.object().shape({
  full_name: Yup.string()
    .nullable()
    .min(2, "Name too short")
    .max(50, "Name too long"),
  email: Yup.string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .email("Invalid email")
    .nullable(),
  phone_number: Yup.string()
    .nullable()
    .test('phone-format', 'Phone number must be 10 digits', (value) => {
      if (!value || value.trim() === '') return true;
      return /^[0-9]{10}$/.test(value);
    }),
  address: Yup.string().nullable(),
  attack_type: Yup.string().required("Attack type is required"),
  system_functions: Yup.array()
    .min(1, "Please select at least one system function")
    .required("This field is required"),
  impact: Yup.string().required("Impact level is required"),
  time: Yup.date()
    .required("Time is required")
    .max(new Date(), "Cannot select future date"),
  affected_systems: Yup.number().required("Number of affected systems is required").min(1, "At least one system must be affected"),
  incident_description: Yup.string()
    .min(50, "Please provide more details (minimum 50 characters)"),
  incident_files: Yup.mixed(),
  recaptcha: Yup.string().required("Please complete the reCAPTCHA verification"),
});

export const copValidationSchema = Yup.object().shape({
  full_name: Yup.string()
    .nullable()
    .min(2, "Name too short")
    .max(50, "Name too long"),
  email: Yup.string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .email("Invalid email")
    .nullable(),
  phone_number: Yup.string()
    .nullable()
    .test('phone-format', 'Phone number must be 10 digits', (value) => {
      if (!value || value.trim() === '') return true;
      return /^[0-9]{10}$/.test(value);
    }),
  address: Yup.string().nullable(),
  incident_type: Yup.string().required("Incident type is required"),
  other_incident_type: Yup.string().when("incident_type", {
    is: "other",
    then: (schema) => schema.required("Please specify the incident type"),
    otherwise: (schema) => schema.notRequired(),
  }),
  platform: Yup.string().required("Platform is required"),
  url: Yup.string().required("URL is required"),
  username: Yup.string().notRequired(),
  victim_age_range: Yup.string().required("Victim age range is required"),
  relationship_to_reporter: Yup.string().required("Relationship to reporter is required"),
  other_relationship: Yup.string().when("relationship_to_reporter", {
    is: "other",
    then: (schema) => schema.required("Please specify the relationship"),
    otherwise: (schema) => schema.notRequired(),
  }),
  victim_location: Yup.string().notRequired(),
  suspect_username: Yup.string().notRequired(),
  suspect_description: Yup.string().notRequired(),
  urgency: Yup.string().required("Urgency level is required"),
  consent_to_share: Yup.boolean().required("Consent to share is required"),
  impact: Yup.string().required("Impact level is required"),
  time: Yup.date()
    .required("Time is required")
    .max(new Date(), "Cannot select future date")
    .min(ONE_YEAR_AGO, "Time cannot be more than 1 year ago"),
  incident_description: Yup.string()
    .min(50, "Please provide more details (minimum 50 characters)"),
  incident_files: Yup.mixed(),
  recaptcha: Yup.string().required("Please complete the reCAPTCHA verification"),
});

export const vulnerabilityValidationSchema = Yup.object().shape({
  full_name: Yup.string()
    .nullable()
    .min(2, "Name too short")
    .max(50, "Name too long"),
  email: Yup.string()
    .email("Invalid email")
    .nullable(),
  phone_number: Yup.string()
    .nullable()
    .matches(/^[0-9+\-\s()]{10,15}$/, "Invalid phone number format"),
  organization: Yup.string()
    .max(100, "Organization name too long"),
  reporter_type: Yup.string().required("Reporter type is required"),
  vulnerability_type: Yup.string().required("Vulnerability type is required"),
  affected_system: Yup.string()
    .required("Affected system is required")
    .min(3, "System name too short"),
  affected_system_entity: Yup.string()
    .required("Affected system entity is required")
    .max(200, "Entity name too long"),
  version_info: Yup.string()
    .max(100, "Version info too long"),
  severity: Yup.string().required("Severity level is required"),
  discovery_method: Yup.string().required("Discovery method is required"),
  affected_components: Yup.string()
    .min(10, "Please provide more details about affected components"),
  steps_to_reproduce: Yup.string()
    .required("Steps to reproduce are required")
    .min(20, "Please provide detailed steps"),
  impact_assessment: Yup.string()
    .min(20, "Please describe the potential impact"),
  suggested_remediation: Yup.string()
    .min(10, "Please provide remediation suggestions if any"),
  proof_of_concept: Yup.string()
    .max(1000, "Proof of concept description too long"),
  supporting_files: Yup.mixed(),
  recaptcha: Yup.string().required("Please complete the reCAPTCHA verification"),
});

export const malwareValidationSchema = Yup.object().shape({
  full_name: Yup.string()
    .nullable()
    .min(2, "Name too short")
    .max(50, "Name too long"),
  email: Yup.string()
    .email("Invalid email")
    .nullable()
    .test("is-company-email", "Please use a company email address (no free/temp emails)", (value) => {
      if (!value) return true;
      const domain = value.split('@')[1]?.toLowerCase();
      return !blockedDomains.includes(domain);
    }),
  phone_number: Yup.string().nullable(),
  organization: Yup.string()
    .required("Organization name is required")
    .max(100, "Organization name too long"),
  reporter_type: Yup.string().required("Reporter type is required"),
  malware_type: Yup.string(),
  detection_method: Yup.string().required("Detection method is required"),
  affected_platform: Yup.string().required("Affected platform is required"),
  first_detection_date: Yup.date()
    .required("First detection date is required")
    .max(new Date(), "Date cannot be in the future"),
  infection_vector: Yup.string().required("Infection vector is required"),
  observed_behavior: Yup.string()
    .min(20, "Please describe the behavior in detail"),
  // Archive password validations
  has_archive_password: Yup.boolean(),
  archive_password: Yup.string().when('has_archive_password', {
    is: true,
    then: (schema) => schema.required('Archive password is required').min(1, 'Password cannot be empty').max(100, 'Password too long'),
    otherwise: (schema) => schema.notRequired(),
  }),
  impact_assessment: Yup.string()
    .min(20, "Please describe the impact"),
  malware_sample: Yup.mixed()
    .required("Malware sample is required")
    .test("fileType", "Only ZIP files are allowed for malware samples", (value) => {
      if (!value || value.length === 0) return false;
      return Array.from(value).every(file =>
        file.type === "application/zip" ||
        file.type === "application/x-zip-compressed" ||
        file.name.toLowerCase().endsWith('.zip')
      );
    })
    .test("fileSize", "File size must be less than 50MB", (value) => {
      if (!value || value.length === 0) return true;
      return Array.from(value).every(file => file.size <= 50 * 1024 * 1024);
    }),
  additional_files: Yup.mixed()
    .test("fileType", "Only document and image files are allowed for additional files", (value) => {
      if (!value || value.length === 0) return true;
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "image/jpeg",
        "image/jpg", 
        "image/png",
        "image/gif"
      ];
      return Array.from(value).every(file => 
        allowedTypes.includes(file.type) || 
        file.name.toLowerCase().match(/\.(pdf|doc|docx|txt|jpg|jpeg|png|gif)$/)
      );
    })
    .test("fileSize", "File size must be less than 10MB each", (value) => {
      if (!value || value.length === 0) return true;
      return Array.from(value).every(file => file.size <= 10 * 1024 * 1024);
    }),
  recaptcha: Yup.string().required("Please complete the reCAPTCHA verification"),
});

export const abuseValidationSchema = Yup.object().shape({
  full_name: Yup.string()
    .nullable()
    .min(2, "Name too short")
    .max(50, "Name too long"),
  email: Yup.string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .email("Invalid email")
    .nullable(),
  phone_number: Yup.string()
    .nullable()
    .matches(/^[0-9+\-\s()]{10,15}$/, "Invalid phone number format"),
  organization: Yup.string()
    .max(100, "Organization name too long"),
  relationship_to_victim: Yup.string().required("Relationship to victim is required"),
  abuse_type: Yup.string().required("Abuse type is required"),
  platform_type: Yup.string().required("Platform type is required"),
  platform_name: Yup.string()
    .max(100, "Platform name too long"),
  incident_url: Yup.string().required("Incident URL is required"),
  perpetrator_username: Yup.string()
    .max(100, "Username too long"),
  victim_username: Yup.string()
    .max(100, "Username too long"),
  incident_date: Yup.date()
    .required("Incident date is required")
    .max(new Date(), "Date cannot be in the future"),
  is_ongoing: Yup.boolean(),
  abuse_description: Yup.string()
    .required("Abuse description is required")
    .min(50, "Please provide more details (minimum 50 characters)"),
  evidence_description: Yup.string()
    .min(20, "Please provide more details about evidence"),
  impact_on_victim: Yup.string()
    .min(20, "Please describe the impact in detail"),
  additional_notes: Yup.string()
    .max(1000, "Additional notes too long"),
  urgency_level: Yup.string().required("Urgency level is required"),
  evidence_files: Yup.mixed()
    .test("fileSize", "File size must be less than 10MB each", (value) => {
      if (!value || value.length === 0) return true;
      return Array.from(value).every(file => file.size <= 10 * 1024 * 1024);
    }),
});